import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ClientAvatar } from "@/components/ClientAvatar";
import {
  Search, MapPin, CornerDownLeft, X,
  LayoutDashboard, CalendarDays, ClipboardList, UserCog, Timer, Users, Music, FileText, Plug, Settings, Workflow,
} from "lucide-react";

const PAGES = [
  { label: "Dashboard", to: "/dashboard", icon: LayoutDashboard, keywords: "home overview" },
  { label: "Calendar", to: "/calendar", icon: CalendarDays, keywords: "schedule agenda shoots" },
  { label: "Orders", to: "/orders", icon: ClipboardList, keywords: "jobs properties" },
  { label: "Workload", to: "/workload", icon: UserCog, keywords: "editors deliverables" },
  { label: "Time Tracking", to: "/time-tracking", icon: Timer, keywords: "timer hours logs" },
  { label: "Clients", to: "/clients", icon: Users, keywords: "customers agents" },
  { label: "Automations", to: "/automations", icon: Workflow, keywords: "rules triggers workflows auto email reminders" },
  { label: "Music", to: "/music", icon: Music, keywords: "tracks audio songs" },
  { label: "Templates", to: "/templates", icon: FileText, keywords: "snippets messages" },
  { label: "Integrations", to: "/integrations", icon: Plug, keywords: "aryeo dropbox connect" },
  { label: "Settings", to: "/settings", icon: Settings, keywords: "account branding sla preferences" },
];

const KIND_LABELS = { page: "Pages", client: "Clients", property: "Properties" };

export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ clients: [], properties: [] });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  const query = q.trim().toLowerCase();
  const pageMatches = query
    ? PAGES.filter((p) => p.label.toLowerCase().includes(query) || (p.keywords || "").includes(query))
    : [];

  // Flatten for keyboard navigation (pages first, then clients, then properties).
  const items = [
    ...pageMatches.map((p) => ({ kind: "page", id: p.to, label: p.label, sub: "Go to page", to: p.to, icon: p.icon })),
    ...results.clients.map((c) => ({ kind: "client", id: c.id, label: c.name, email: c.email, sub: c.email || `${c.orders_count} propert${c.orders_count === 1 ? "y" : "ies"}` })),
    ...results.properties.map((p) => ({ kind: "property", id: p.order_id, label: p.address, client: p.client_name, sub: p.client_name || (p.number ? `#${p.number}` : "") })),
  ];

  useEffect(() => {
    if (q.trim().length < 1) { setResults({ clients: [], properties: [] }); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q.trim())}`);
        setResults(data);
        setActive(0);
      } catch (err) { console.error("Search failed", err); }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  // "/" focuses the search unless typing in a field.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "/") return;
      const el = document.activeElement;
      const typing = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
      if (typing) return;
      e.preventDefault();
      inputRef.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Close on outside click.
  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = useCallback((item) => {
    if (!item) return;
    setOpen(false); setQ("");
    if (item.kind === "page") navigate(item.to);
    else if (item.kind === "client") navigate(`/clients/${encodeURIComponent(item.id)}`);
    else navigate(`/orders/${item.id}`);
  }, [navigate]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((i) => (items.length ? (i + 1) % items.length : 0)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => (items.length ? (i - 1 + items.length) % items.length : 0)); }
    else if (e.key === "Enter") { e.preventDefault(); go(items[active] || items[0]); }
    else if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); }
  };

  const showDropdown = open && q.trim().length >= 1;

  return (
    <div ref={boxRef} className="relative w-full max-w-xl" data-testid="global-search">
      {open && createPortal(
        <div
          onMouseDown={() => setOpen(false)}
          data-testid="search-overlay"
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px] animate-in fade-in duration-150"
        />,
        document.body,
      )}
      <div className="relative z-50">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          ref={inputRef}
          data-testid="global-search-input"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search clients & properties…"
          className="h-9 w-full rounded-lg border border-border bg-secondary/40 pl-9 pr-9 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary/50 focus:bg-background"
        />
        {q ? (
          <button onClick={() => { setQ(""); inputRef.current?.focus(); }} data-testid="global-search-clear"
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        ) : (
          <kbd className="absolute right-2.5 top-1/2 hidden -translate-y-1/2 rounded border border-border bg-background px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground sm:block">/</kbd>
        )}
      </div>

      {showDropdown && (
        <div className="absolute left-0 right-0 top-11 z-[60] overflow-hidden rounded-xl border border-border bg-popover shadow-2xl" data-testid="global-search-results">
          {loading && items.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">Searching…</div>
          ) : items.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground" data-testid="global-search-empty">No matches for "{q}"</div>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto py-1">
              {items.map((item, idx) => {
                const isPage = item.kind === "page";
                const isClient = item.kind === "client";
                const showHeader = idx === 0 || items[idx - 1].kind !== item.kind;
                const Icon = isPage ? item.icon : MapPin;
                return (
                  <div key={`${item.kind}-${item.id}`}>
                    {showHeader && <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{KIND_LABELS[item.kind]}</div>}
                    <button
                      data-testid={`search-result-${idx}`}
                      onMouseEnter={() => setActive(idx)}
                      onClick={() => go(item)}
                      className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors ${active === idx ? "bg-secondary" : "hover:bg-secondary/50"}`}
                    >
                      {isClient ? (
                        <ClientAvatar name={item.label} email={item.email} className="h-7 w-7 text-[10px]" testId={`search-client-avatar-${idx}`} />
                      ) : (
                        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${isPage ? "bg-cyan-500/15 text-cyan-400" : "bg-primary/15 text-primary"}`}>
                          <Icon className="h-3.5 w-3.5" />
                        </span>
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">{item.label}</span>
                        {item.sub && (
                          <span className="flex items-center gap-1.5 truncate text-xs text-muted-foreground">
                            {item.client && <ClientAvatar name={item.client} className="h-3.5 w-3.5 text-[6px]" />}
                            <span className="truncate">{item.sub}</span>
                          </span>
                        )}
                      </span>
                      {active === idx && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
